/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import shaka from "shaka-player/dist/shaka-player.compiled";

import {
  ClosedCaptionPreset,
  DrmType,
  PlayerErrorSeverity,
  PlayerState,
  SHAKA_EVENT_NAME
} from "../enums";
import { getWebErrorCode, PLAYER_ERROR_SEPARATOR } from "../error";
import type { IPlayer } from "../interfaces";
import { Log } from "../../logging/Log";
import { Diagnostics } from "../../logging";
import { DateTime } from "../../utility/DateTime";
import { MILLISECONDS_IN_A_SECOND } from "../../../config";
import type {
  TActiveDrmInfo,
  TAudioTrack,
  TFairPlayConfig,
  TPlayerDrmConfig,
  TPlayerError,
  TPlayerEventMap,
  TPlayerSourceConfig,
  TPlayerStats,
  TSeekRange,
  TTextTrack,
  TVariantTrack,
  TWidevineConfig
} from "../types";
import { ShakaPlayerUtils } from "../utils";
import { ClosedCaptionStyler } from "./ClosedCaptionStyler";
import { DEFAULT_CLOSED_CAPTION_PRESET } from "./ClosedCaptionStyleConfig";
import {
  SHAKA_PLAYER_CONFIG,
  TIMING_RETRY_OVERRIDES
} from "./ShakaPlayerConfig";

// Install EME polyfills for legacy Smart TV browsers.
shaka.polyfill.installAll();

type TDrmRequestFilter = (
  type: number,
  request: shaka.extern.Request
) => void | Promise<void>;

type TListenerFn<K extends keyof TPlayerEventMap> = (
  event: TPlayerEventMap[K]
) => void;

/**
 * Concrete implementation of IPlayer using Shaka Player.
 *
 * Lifecycle:
 *   1. init(videoEl, containerEl) — attach to DOM elements
 *   2. load(config)               — load a media source
 *   3. play / pause / seek        — control playback
 *   4. unload()                   — release the current source
 *   5. destroy()                  — tear down entirely
 */
export class ShakaVideoPlayerImpl implements IPlayer {
  private static readonly TAG: string = "ShakaVideoPlayerImpl";

  private mPlayer: shaka.Player | null = null;
  private mVideoEl: HTMLVideoElement | null = null;
  // mContainerEl is retained for future use (UI overlays / captions layer).
  private mContainerEl: HTMLDivElement | null = null;
  private mState: PlayerState = PlayerState.IDLE;
  private mDrmConfig: TPlayerDrmConfig | null = null;
  private mPreSeekState: PlayerState = PlayerState.IDLE;
  private mFirstFrameFired: boolean = false;

  /**
   * Runtime styler for the Shaka caption `::cue` rule. Created on init() so
   * closed caption appearance can be changed in response to user selection.
   */
  private mCcStyler: ClosedCaptionStyler | null = null;

  /**
   * Tracks whether the manifest was live at load time so we can detect
   * dynamic-to-static transitions for EOS handling.
   */
  private mWasLiveAtLoad: boolean = false;

  /**
   * Guard preventing multiple dynamic-to-static restart attempts.
   */
  private mDynamicToStaticRestartAttempted: boolean = false;

  /**
   * Id of the audio track last reported active. Live manifest refreshes
   * (e.g. Start Over) can rebuild Shaka's variant objects, after which no
   * variant is flagged active until ABR next switches. This id lets
   * getAudioTracks() keep reporting the playing track through that gap.
   */
  private mLastActiveAudioId: number | null = null;

  /**
   * Seconds from the end of a VOD asset at which EOS is considered reached.
   * Accounts for imprecise segment boundaries / rounding.
   */
  private static readonly EOS_THRESHOLD_SECONDS: number = 0.5;

  /**
   * Seconds from the seekable range boundaries at which trick-play mode is
   * automatically exited and playback is reset to normal speed (1×).
   */
  private static readonly TRICK_PLAY_BOUNDARY_THRESHOLD: number = 1.0;

  /**
   * Maximum number of automatic recovery attempts for a recoverable error
   * before the failure is treated as fatal. Mirrors kaveri's RETRY_LIMIT.
   */
  private static readonly RETRY_LIMIT: number = 5;

  /**
   * Delay (ms) before replaying after a recoverable error, giving the
   * transient condition time to clear before the reload attempt.
   */
  private static readonly RETRY_DELAY_MS: number = 2000;

  /**
   * Grace window (ms) a network error is withheld for, giving the content
   * already in the buffer time to play out and Shaka's own recovery (variant
   * switch / retryStreaming) time to succeed unseen. Mirrors kaveri's
   * FIFTY_SECONDS.
   */
  private static readonly NETWORK_ERROR_GRACE_MS: number = 50000;

  /**
   * Shortened grace window (ms) used once a recovery attempt has already been
   * spent on the current failure — the stream has proven unreliable, so wait
   * less before acting. Mirrors kaveri's THIRTY_SECONDS.
   */
  private static readonly NETWORK_ERROR_GRACE_RETRY_MS: number = 30000;

  /**
   * Buffer remaining ahead of the playhead (seconds) below which the buffer
   * counts as drained. Matches SHAKA_PLAYER_CONFIG.streaming.rebufferingGoal:
   * with less than this buffered, Shaka can no longer keep playing.
   */
  private static readonly NETWORK_ERROR_BUFFER_FLOOR_SECONDS: number = 1;

  /**
   * Interval (ms) at which a withheld network error re-checks the buffer. A
   * timer rather than the TIME_UPDATE handler because timeupdate stops firing
   * exactly when the buffer runs dry, which is the moment being watched for.
   */
  private static readonly NETWORK_ERROR_POLL_MS: number = 1000;

  /**
   * First delay (seconds) before Shaka retries streaming a live stream whose
   * segment or manifest requests keep failing. Doubles on every consecutive
   * failure up to {@link LIVE_STREAMING_RETRY_MAX_DELAY_S}. Shaka's default
   * retries after a flat 0.1s under lowLatencyMode, with no limit.
   */
  private static readonly LIVE_STREAMING_RETRY_BASE_DELAY_S: number = 1;

  /** Ceiling (seconds) for the live streaming retry backoff. */
  private static readonly LIVE_STREAMING_RETRY_MAX_DELAY_S: number = 8;

  /**
   * Consecutive live streaming failures Shaka has handed to the failure
   * callback since the last segment was fetched successfully.
   */
  private mLiveStreamingFailureCount: number = 0;

  /** Number of automatic recovery attempts made for the current failure. */
  private mRetryCount: number = 0;

  /** Playback position (seconds) captured before a recovery reload. */
  private mRetrySavedPosition: number = 0;

  /**
   * The most recently loaded source config, retained so a recovery attempt
   * can replay the same source — load() itself does not keep it.
   */
  private mLastSourceConfig: TPlayerSourceConfig | null = null;

  /** Pending recovery-reload timer, cleared on success / unload / destroy. */
  private mRetryTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * True while a recovery reload is in flight. Guards load()/unload() from
   * resetting the retry budget or discarding the retained source config
   * during a replay.
   */
  private mIsRetrying: boolean = false;

  /**
   * Advanced by every load() and unload() from outside the player. A recovery
   * replay captures it and stands down once it moves: the source it was
   * replaying has been replaced or torn down, so neither its play() nor a
   * further retry may follow.
   */
  private mRecoveryGeneration: number = 0;

  /**
   * Advanced by every load and every outside unload. A load that resumes from
   * an await under an older value has been superseded and stops there, so the
   * newest request is always the one that ends up loaded.
   */
  private mLoadSequence: number = 0;

  /**
   * The unload in progress, if any. A load waits for it: the unload resets the
   * player configuration once it completes, which would otherwise wipe the
   * DRM configuration the load has just applied.
   */
  private mPendingUnload: Promise<void> | null = null;

  /**
   * A network error being withheld while there is still buffered content to
   * play. Null when no network error is currently deferred.
   */
  private mDeferredNetworkError: TPlayerError | null = null;

  /** Epoch ms at which a deferred network error stops being withheld. */
  private mNetworkErrorDeadlineMs: number = 0;

  /** Poll timer watching the buffer while a network error is deferred. */
  private mNetworkErrorPollTimerId: ReturnType<typeof setInterval> | null =
    null;

  /**
   * Typed application-level event listeners registered via on() / off().
   * `any` is intentional here: the Map is keyed by keyof TPlayerEventMap and each
   * entry holds listeners for that specific event. The generic K is resolved
   * at call sites via the typed on()/off() signatures; the internal storage
   * must use `any` because TypeScript cannot correlate Map key types with
   * their corresponding value types in a heterogeneous collection.
   */
  private mListeners: Map<
    keyof TPlayerEventMap,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Set<TListenerFn<any>>
  > = new Map();

  /**
   * Shaka-level event listeners stored so they can be removed on destroy().
   */
  private mShakaEventListeners: Map<string, (event: Event) => void> = new Map();

  /**
   * HTMLVideoElement event listeners stored so they can be removed on destroy().
   */
  private mVideoEventListeners: Map<string, (event: Event) => void> = new Map();

  /**
   * The currently registered DRM request filter, kept so it can be
   * cleanly unregistered when the token is refreshed or the player is destroyed.
   */
  private mDrmRequestFilter: TDrmRequestFilter | null = null;

  /**
   * The currently registered playback URL request filter, tracked so it can
   * be replaced when load() is called again with different URL request headers.
   */
  private mPlaybackUrlRequestFilter: TDrmRequestFilter | null = null;

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](ShakaVideoPlayerImpl.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      ShakaVideoPlayerImpl.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        ShakaVideoPlayerImpl.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  public init(videoEl: HTMLVideoElement, containerEl: HTMLDivElement): void {
    this.mVideoEl = videoEl;
    this.mContainerEl = containerEl;
    this.mPlayer = new shaka.Player(videoEl, containerEl);
    this.applyBaseConfiguration();
    this.registerTimingRetryClamp();
    this.registerLiveStreamingFailureReset();
    this.registerShakaEventListeners();
    this.mCcStyler = new ClosedCaptionStyler();
    this.mCcStyler.applyPreset(DEFAULT_CLOSED_CAPTION_PRESET);
    this.transitionState(PlayerState.IDLE);
    this.log("debug", "init(): Player initialized");
  }

  public async load(config: TPlayerSourceConfig): Promise<void> {
    this.supersedeRecovery();
    return this.loadSource(config);
  }

  public async unload(): Promise<void> {
    this.supersedeRecovery();
    // An unload also supersedes any load still in flight.
    this.mLoadSequence += 1;
    return this.unloadSource();
  }

  /**
   * A load or unload from outside the player replaces whatever a recovery
   * replay was working on. Without this, an unload landing mid-replay was
   * taken for the replay's own unload: it kept the retry state and skipped
   * IDLE, leaving the player in ERROR_RECOVERY with the loading indicator up.
   */
  private supersedeRecovery(): void {
    this.mRecoveryGeneration += 1;
    this.mIsRetrying = false;
  }

  private async loadSource(config: TPlayerSourceConfig): Promise<void> {
    if (this.mPlayer === null || this.mVideoEl === null) {
      const msg = "load(): Player not initialized. Call init() first.";
      this.log("error", msg);
      throw new Error(msg);
    }
    this.mLoadSequence += 1;
    const loadSequence: number = this.mLoadSequence;

    if (!(await this.clearForSource(loadSequence))) {
      return;
    }

    this.mFirstFrameFired = false;
    this.mWasLiveAtLoad = false;
    this.mDynamicToStaticRestartAttempted = false;
    this.mLastActiveAudioId = null;

    // Retain the config so a recovery attempt can replay this source, and
    // reset the retry budget for genuine user-initiated loads.
    this.prepareRetryStateForLoad(config);

    this.emitEvent(PlayerState.INITIATION, { timestamp: Date.now() });

    const playbackConfig = config.playbackConfig;
    if (playbackConfig !== null && playbackConfig !== undefined) {
      if (playbackConfig.muted !== null && playbackConfig.muted !== undefined) {
        this.mVideoEl.muted = playbackConfig.muted;
      }
      if (
        playbackConfig.autoplay !== null &&
        playbackConfig.autoplay !== undefined
      ) {
        this.mVideoEl.autoplay = playbackConfig.autoplay;
      }
    }

    if (config.drmConfig !== null && config.drmConfig !== undefined) {
      this.mDrmConfig = config.drmConfig;
      this.configureDrm(config.drmConfig);
    }

    if (
      playbackConfig !== null &&
      playbackConfig !== undefined &&
      playbackConfig.playbackUrlRequestHeaders !== null &&
      playbackConfig.playbackUrlRequestHeaders !== undefined
    ) {
      this.clearPlaybackUrlRequestFilter();
      this.configurePlaybackUrlHeaders(
        playbackConfig.playbackUrlRequestHeaders
      );
    }

    const startPosition =
      playbackConfig !== null &&
      playbackConfig !== undefined &&
      playbackConfig.startPosition !== null &&
      playbackConfig.startPosition !== undefined
        ? playbackConfig.startPosition
        : null;

    this.log("debug", "load(): Loading source: " + config.url);

    try {
      await this.mPlayer.load(config.url, startPosition);
      if (loadSequence !== this.mLoadSequence) {
        return;
      }
      this.mWasLiveAtLoad = this.mPlayer.isLive();
    } catch (error: unknown) {
      const failure = ShakaVideoPlayerImpl.interpretLoadFailure(error);
      if (failure === null || loadSequence !== this.mLoadSequence) {
        return;
      }
      this.handleLoadFailure(failure);
      throw failure;
    }
  }

  /**
   * Clears the way for a new source: unloads the current one and waits out
   * any unload already in progress.
   *
   * @returns False when a newer load or unload superseded this load meanwhile.
   */
  private async clearForSource(loadSequence: number): Promise<boolean> {
    if (
      this.mState !== PlayerState.IDLE &&
      this.mState !== PlayerState.DESTROYED
    ) {
      this.log(
        "debug",
        "load(): Unloading previous source before loading new one"
      );
      await this.unloadSource();
    }
    if (this.mPendingUnload !== null) {
      await this.mPendingUnload.catch(() => undefined);
    }
    if (loadSequence !== this.mLoadSequence) {
      this.log("debug", "load(): superseded by a newer request");
      return false;
    }
    return true;
  }

  private unloadSource(): Promise<void> {
    const unloading: Promise<void> = this.runUnload();
    this.mPendingUnload = unloading;
    const settle = (): void => {
      if (this.mPendingUnload === unloading) {
        this.mPendingUnload = null;
      }
    };
    unloading.then(settle, settle);
    return unloading;
  }

  private async runUnload(): Promise<void> {
    if (this.mPlayer === null) {
      this.log("warn", "unload(): Player not initialized, nothing to unload.");
      return;
    }

    this.log("debug", "unload(): Unloading source");
    this.clearDrmRequestFilter();
    this.clearPlaybackUrlRequestFilter();
    this.mDrmConfig = null;
    this.mFirstFrameFired = false;
    this.mWasLiveAtLoad = false;
    this.mDynamicToStaticRestartAttempted = false;
    this.mLastActiveAudioId = null;
    // Cancel any pending recovery and drop the retained source — unless this
    // unload is the one a recovery replay itself triggered (mIsRetrying), in
    // which case the budget and config must survive into the reload.
    if (!this.mIsRetrying) {
      this.clearRetryTimer();
      this.clearNetworkErrorDeferral();
      this.mLastSourceConfig = null;
      this.mRetryCount = 0;
      this.mRetrySavedPosition = 0;
      this.mLiveStreamingFailureCount = 0;
    }
    try {
      // A recovery replay unloads on its way back to loading. Staying in
      // ERROR_RECOVERY across it keeps the player in one continuous "working
      // on it" state, so the loading indicator does not blink off through
      // IDLE halfway through the retry.
      if (!this.mIsRetrying) {
        this.transitionState(PlayerState.IDLE);
      }
      await this.mPlayer.unload();
    } catch (error: unknown) {
      this.log("error", "unload()", error);
      throw error;
    }

    // Deliberately after the unload, not before it. Configuring the player
    // while a manifest is still loaded makes Shaka re-filter that manifest's
    // variants against the codec preferences, which throws on a manifest
    // carrying a variant with no audio or no video track — HLS, in practice.
    // Thrown from here it aborted the unload itself, leaving the previous
    // source loaded so the next load() failed the same way and the channel
    // never changed. With nothing loaded there is nothing to re-filter.
    this.resetConfiguration();
  }

  public async destroy(): Promise<void> {
    if (this.mPlayer === null) {
      this.log(
        "warn",
        "destroy(): Player not initialized, nothing to destroy."
      );
      return;
    }

    this.log("debug", "destroy(): Destroying player");

    this.unregisterShakaEventListeners();
    this.clearDrmRequestFilter();
    this.clearPlaybackUrlRequestFilter();
    this.clearRetryTimer();
    this.clearNetworkErrorDeferral();

    if (this.mCcStyler !== null) {
      this.mCcStyler.destroy();
      this.mCcStyler = null;
    }

    try {
      await this.mPlayer.destroy();
    } catch (error: unknown) {
      this.log("error", "destroy()", error);
      throw error;
    } finally {
      this.transitionState(PlayerState.DESTROYED);
      this.mPlayer = null;
      this.mVideoEl = null;
      this.mContainerEl = null;
      this.mDrmConfig = null;
      this.mLastSourceConfig = null;
      this.mRetryCount = 0;
      this.mRetrySavedPosition = 0;
      this.mIsRetrying = false;
      this.mListeners.clear();
      this.mShakaEventListeners.clear();
      this.mVideoEventListeners.clear();
    }
  }

  // ── Playback Control ───────────────────────────────────────────────────────

  public play(): void {
    if (this.mVideoEl === null) {
      this.log("warn", "play(): Video element not available.");
      return;
    }
    this.mVideoEl.play().catch((error: unknown) => {
      this.log("error", "play()", error);
      this.settleRejectedPlay(error);
    });
  }

  /**
   * Moves a player still waiting to start into PAUSED once play() is refused.
   *
   * READY, BUFFERING and SEEKING are only left through a `playing`, `seeked`
   * or `buffering: false` that a refused play() never produces, so without
   * this the loading indicator stays up over a stream that will not start
   * until the user presses play. An AbortError is left alone: it means a
   * newer load() or pause() superseded this request, and that one now owns
   * the state.
   *
   * @param error - The play() rejection.
   */
  private settleRejectedPlay(error: unknown): void {
    const isAborted =
      typeof error === "object" &&
      error !== null &&
      (error as { name?: unknown }).name === "AbortError";
    if (isAborted || this.mVideoEl === null || !this.mVideoEl.paused) {
      return;
    }
    if (
      this.mState === PlayerState.READY ||
      this.mState === PlayerState.BUFFERING ||
      this.mState === PlayerState.SEEKING
    ) {
      this.transitionState(PlayerState.PAUSED);
    }
  }

  public pause(): void {
    if (this.mVideoEl === null) {
      this.log("warn", "pause(): Video element not available.");
      return;
    }
    this.mVideoEl.pause();
  }

  public seek(positionSeconds: number): void {
    if (this.mVideoEl === null) {
      this.log("warn", "seek(): Video element not available.");
      return;
    }
    // Takes a position in the same clock the getters report, so a caller can
    // seek to a time it read back without converting anything. Both steps
    // happen in player time, which is what actually gets written.
    this.mVideoEl.currentTime = this.clampToSeekableRange(
      this.toRawTime(positionSeconds)
    );
  }

  public setTrickPlayRate(rate: number, useTrickPlayTrack?: boolean): void {
    if (this.mPlayer === null) {
      this.log("warn", "setTrickPlayRate(): Player not initialized.");
      return;
    }
    if (isNaN(rate) || rate === 0) {
      this.log("warn", "setTrickPlayRate(): Invalid rate.");
      return;
    }

    if (rate === 1) {
      this.log("warn", "setTrickPlayRate(): Rate is 1, no trick play needed.");
      this.setMuted(false);
    } else {
      this.setMuted(true);
    }

    this.mPlayer.trickPlay(rate, useTrickPlayTrack);
  }

  public cancelTrickPlay(): void {
    if (this.mPlayer === null) {
      this.log("warn", "cancelTrickPlay(): Player not initialized.");
      return;
    }
    this.mPlayer.cancelTrickPlay();
    this.setMuted(false);
  }

  // ── Time & Range ───────────────────────────────────────────────────────────

  public getCurrentTime(): number {
    return this.toNormalizedTime(this.getRawCurrentTime());
  }

  public getDuration(): number {
    if (this.mVideoEl === null) {
      return 0;
    }
    return this.mVideoEl.duration;
  }

  public getSeekableRange(): TSeekRange {
    const range = this.getRawSeekableRange();
    const offset = this.getTimebaseOffsetSeconds();

    // The span is unchanged by a shift of origin, so it is carried across
    // rather than recomputed — that keeps it exact even if the offset moved
    // between reading the two bounds.
    return {
      start: range.start + offset,
      end: range.end + offset,
      duration: range.duration
    };
  }

  // ── Timebase normalization ─────────────────────────────────────────────────

  /**
   * The player's own position, in whatever clock the manifest uses.
   *
   * Not for callers — {@link getCurrentTime} is the public contract. This
   * exists so the seek path can work in the clock it actually writes to.
   */
  private getRawCurrentTime(): number {
    if (this.mVideoEl === null) {
      return 0;
    }
    return this.mVideoEl.currentTime;
  }

  /** The player's own seekable range, in the manifest's clock. */
  private getRawSeekableRange(): TSeekRange {
    if (this.mPlayer === null) {
      return { start: 0, end: 0, duration: 0 };
    }
    const range = this.mPlayer.seekRange();
    return {
      start: range.start,
      end: range.end,
      duration: range.end - range.start
    };
  }

  /**
   * Seconds to add to a player time to express it as a wall-clock epoch.
   *
   * The player's clock is a property of the manifest, not of what is playing:
   * DASH reports live positions as epoch seconds but start over, catch up,
   * VOD and nDVR as offsets from zero, while HLS reports offsets from zero
   * for all of them. Consumers that reason about wall-clock time — the seek
   * bar's live labels, the programme-boundary checks, the start-over lower
   * bound — cannot each re-derive which they were handed, so the difference
   * is resolved once, here.
   *
   * Zero unless the stream is live and the clock is not already epoch, which
   * is what makes this a no-op for every case that works today.
   *
   * @returns The offset in seconds; `0` when no conversion applies.
   */
  private getTimebaseOffsetSeconds(): number {
    if (this.mPlayer === null || !this.isLive()) {
      return 0;
    }

    // Read from the seek range, not the playhead. The clock is a property of
    // the manifest timeline, which is epoch as soon as the manifest is
    // parsed, whereas `currentTime` stays at 0 until the playhead is
    // positioned. Testing the playhead would read that gap as an
    // offset-based clock and shift an already-epoch DASH range a second time.
    // The live edge is used because it is always the larger bound.
    if (DateTime.isEpochTimestamp(this.getRawSeekableRange().end)) {
      return 0;
    }

    const playheadDate = this.mPlayer.getPlayheadTimeAsDate();

    // Before the first frame there is no playhead to anchor to. Left
    // unconverted rather than anchored to Date.now(): a raw value is one a
    // caller can still recognise and recover from, where a fabricated one
    // looks authoritative and silently misplaces the playhead.
    if (playheadDate === null || playheadDate === undefined) {
      return 0;
    }

    return (
      playheadDate.getTime() / MILLISECONDS_IN_A_SECOND -
      this.getRawCurrentTime()
    );
  }

  /** Converts a player time to the public contract's clock. */
  private toNormalizedTime(rawSeconds: number): number {
    return rawSeconds + this.getTimebaseOffsetSeconds();
  }

  /** Converts a time in the public contract's clock back to player time. */
  private toRawTime(normalizedSeconds: number): number {
    return normalizedSeconds - this.getTimebaseOffsetSeconds();
  }

  public getBufferedRanges(): TimeRanges {
    if (this.mVideoEl === null) {
      return {
        length: 0,
        start: () => 0,
        end: () => 0
      } as TimeRanges;
    }
    return this.mVideoEl.buffered;
  }

  public getBufferAheadSeconds(): number {
    if (this.mVideoEl === null) {
      return 0;
    }

    // Deliberately the element's own clock on both sides. getCurrentTime()
    // normalizes live positions to wall-clock epoch, which would make this
    // subtraction produce a nonsense figure in the billions of seconds.
    const position: number = this.mVideoEl.currentTime;
    const buffered: TimeRanges = this.mVideoEl.buffered;

    for (let i: number = 0; i < buffered.length; i += 1) {
      const start: number = buffered.start(i);
      const end: number = buffered.end(i);
      if (position >= start && position <= end) {
        return Math.max(0, end - position);
      }
    }

    return 0;
  }

  // ── State ──────────────────────────────────────────────────────────────────

  public getState(): PlayerState {
    return this.mState;
  }

  public isLive(): boolean {
    if (this.mPlayer === null) {
      return false;
    }
    return this.mPlayer.isLive();
  }

  public isPaused(): boolean {
    if (this.mVideoEl === null) {
      return true;
    }
    return this.mVideoEl.paused;
  }

  // ── Volume ─────────────────────────────────────────────────────────────────

  public getVolume(): number {
    if (this.mVideoEl === null) {
      return 0;
    }
    return this.mVideoEl.volume;
  }

  public setVolume(volume: number): void {
    if (this.mVideoEl === null) {
      this.log("warn", "setVolume(): Video element not available.");
      return;
    }
    this.mVideoEl.volume = Math.max(0, Math.min(1, volume));
  }

  public isMuted(): boolean {
    if (this.mVideoEl === null) {
      return false;
    }
    return this.mVideoEl.muted;
  }

  public setMuted(muted: boolean): void {
    if (this.mVideoEl === null) {
      this.log("warn", "setMuted(): Video element not available.");
      return;
    }
    this.mVideoEl.muted = muted;
  }

  // ── Audio Tracks ───────────────────────────────────────────────────────────

  public getAudioTracks(): TAudioTrack[] {
    if (this.mPlayer === null) {
      return [];
    }
    const variantTracks = this.mPlayer.getVariantTracks();
    const audioMap = new Map<number, TAudioTrack>();

    for (const track of variantTracks) {
      if (track.audioId === null && track.id === null) {
        continue;
      }
      const audioId: number = track.audioId !== null ? track.audioId : track.id;
      const existing = audioMap.get(audioId);
      if (existing === undefined) {
        audioMap.set(audioId, {
          id: audioId,
          active: track.active,
          codec: track.audioCodec,
          language: track.language,
          channelsCount: track.channelsCount,
          label: track.label,
          primary: track.primary,
          roles: track.roles,
          kind: track.kind
        });
      } else if (track.active) {
        // A given audioId maps to multiple variant tracks (one per video
        // bandwidth/resolution); only the currently-playing variant is
        // flagged active. Mark the collapsed audio track active if any of
        // its variants is the active one.
        existing.active = true;
      }
    }

    const audioTracks: TAudioTrack[] = Array.from(audioMap.values());
    const activeTrack = audioTracks.find((item) => item.active);
    if (activeTrack !== undefined) {
      this.mLastActiveAudioId = activeTrack.id;
    } else if (this.mLastActiveAudioId !== null) {
      // No variant is flagged active (transient after a live manifest
      // refresh) — keep reporting the last active track while it exists.
      const lastActiveTrack = audioMap.get(this.mLastActiveAudioId);
      if (lastActiveTrack !== undefined) {
        lastActiveTrack.active = true;
      }
    }

    return audioTracks;
  }

  public setAudioTrack(track: TAudioTrack): void {
    if (this.mPlayer === null) {
      this.log("warn", "setAudioTrack(): Player not initialized.");
      return;
    }

    const variants = this.mPlayer.getVariantTracks();
    const activeVariant = variants.find((item) => item.active);
    const matchingVariants = variants.filter(
      (item) => item.audioId === track.id
    );

    if (matchingVariants.length === 0) {
      this.log(
        "warn",
        `setAudioTrack(): Track with audioId ${String(track.id)} not found.`
      );
      return;
    }

    const preferredVariant =
      activeVariant !== undefined
        ? matchingVariants.find(
            (item) => item.bandwidth === activeVariant.bandwidth
          ) || matchingVariants[0]
        : matchingVariants[0];

    this.mPlayer.selectVariantTrack(preferredVariant, true);
    this.mLastActiveAudioId = track.id;
  }

  public setAudioTrackByLanguage(language: string): void {
    if (this.mPlayer === null) {
      this.log("warn", "setAudioTrackByLanguage(): Player not initialized.");
      return;
    }

    const audioTracks = this.mPlayer.getAudioTracks();
    const matchingTrack = audioTracks.find(
      (track) => track.language === language
    );
    if (matchingTrack !== null && matchingTrack !== undefined) {
      this.mPlayer.selectAudioTrack(matchingTrack);
      // The new track's id is only known once a variant reports active.
      this.mLastActiveAudioId = null;
    } else {
      this.log(
        "warn",
        `setAudioTrackByLanguage(): Audio track with language ${language} not found.`
      );
    }
  }

  // ── Text Tracks ────────────────────────────────────────────────────────────

  public getTextTracks(): TTextTrack[] {
    if (this.mPlayer === null) {
      return [];
    }
    return this.mPlayer.getTextTracks().map(
      (track): TTextTrack => ({
        id: track.id,
        active: track.active,
        kind: track.kind !== null ? track.kind : "subtitle",
        language: track.language,
        label: track.label,
        mimeType: track.mimeType,
        primary: track.primary
      })
    );
  }

  public setTextTrack(track: TTextTrack): void {
    if (this.mPlayer === null) {
      this.log("warn", "setTextTrack(): Player not initialized.");
      return;
    }
    const shakaTrack = this.mPlayer
      .getTextTracks()
      .find((t) => t.id === track.id);
    if (shakaTrack !== null && shakaTrack !== undefined) {
      this.mPlayer.selectTextTrack(shakaTrack);
    } else {
      this.log("warn", `setTextTrack(): Track with id ${track.id} not found.`);
    }
  }

  public setTextTrackByLanguage(language: string, kind?: string): number {
    if (this.mPlayer === null) {
      this.log("warn", "setTextTrackByLanguage(): Player not initialized.");
      return -1;
    }
    const matched = this.mPlayer.getTextTracks().find((t) => {
      const languageMatches = t.language === language;
      if (kind !== null && kind !== undefined) {
        return languageMatches && t.kind === kind;
      }
      return languageMatches;
    });

    if (matched === null || matched === undefined) {
      return -1;
    }

    this.mPlayer.selectTextTrack(matched);
    return matched.id;
  }

  public setTextTrackVisibility(visible: boolean): void {
    if (this.mPlayer === null) {
      this.log("warn", "setTextTrackVisibility(): Player not initialized.");
      return;
    }
    if (visible) {
      // Shaka v5 removed Player.setTextTrackVisibility(); selecting a track
      // is what makes captions render, so re-select the active track (the
      // enable flows select a specific track right after this call).
      const active = this.mPlayer.getTextTracks().find((t) => t.active);
      if (active !== null && active !== undefined) {
        this.mPlayer.selectTextTrack(active);
      }
    } else {
      // Passing null unloads the active text track, which hides captions.
      this.mPlayer.selectTextTrack(null as unknown as shaka.extern.TextTrack);
    }
  }

  public isTextTrackSupported(): boolean {
    if (this.mPlayer === null) {
      return false;
    }
    return this.mPlayer.getTextTracks().length > 0;
  }

  public setClosedCaptionPreset(preset: ClosedCaptionPreset): void {
    if (this.mCcStyler === null) {
      this.log("warn", "setClosedCaptionPreset(): Player not initialized.");
      return;
    }
    this.mCcStyler.applyPreset(preset);
  }

  public resetClosedCaptionStyle(): void {
    if (this.mCcStyler === null) {
      this.log("warn", "resetClosedCaptionStyle(): Player not initialized.");
      return;
    }
    this.mCcStyler.resetPreset();
  }

  public getClosedCaptionPreset(): ClosedCaptionPreset | null {
    if (this.mCcStyler === null) {
      return null;
    }
    return this.mCcStyler.getCurrentPreset();
  }

  // ── Variant Tracks (Shaka-specific) ────────────────────────────────────────

  public getVariantTracks(): TVariantTrack[] {
    if (this.mPlayer === null) {
      return [];
    }
    return this.mPlayer
      .getVariantTracks()
      .map((track) => this.mapShakaTrackToVariantTrack(track));
  }

  public selectVariantTrack(track: TVariantTrack, clearBuffer?: boolean): void {
    if (this.mPlayer === null) {
      this.log("warn", "selectVariantTrack(): Player not initialized.");
      return;
    }
    const shakaTrack = this.mPlayer
      .getVariantTracks()
      .find((t) => t.id === track.id);
    if (shakaTrack !== null && shakaTrack !== undefined) {
      this.mPlayer.selectVariantTrack(shakaTrack, clearBuffer);
    } else {
      this.log(
        "warn",
        `selectVariantTrack(): Track with id ${track.id} not found.`
      );
    }
  }

  // ── DRM ────────────────────────────────────────────────────────────────────

  public updateDrmToken(config: TPlayerDrmConfig): void {
    if (this.mPlayer === null) {
      this.log("warn", "updateDrmToken(): Player not initialized.");
      return;
    }
    this.mDrmConfig = config;
    this.clearDrmRequestFilter();
    this.configureDrm(config);
    this.log("debug", "updateDrmToken(): DRM token updated");
  }

  public getDrmInfo(): TPlayerDrmConfig | null {
    return this.mDrmConfig;
  }

  public getActiveDrmInfo(): TActiveDrmInfo | null {
    if (this.mPlayer === null) {
      return null;
    }
    try {
      const info = this.mPlayer.drmInfo();
      if (info === null || info === undefined) {
        return null;
      }
      return {
        audioRobustness: info.audioRobustness,
        encryptionScheme: info.encryptionScheme,
        keySystem: info.keySystem,
        videoRobustness: info.videoRobustness
      };
    } catch (error: unknown) {
      this.log("warn", "getActiveDrmInfo()", error);
      return null;
    }
  }

  public getKeyStatuses(): Record<string, string> {
    if (this.mPlayer === null) {
      return {};
    }
    try {
      return this.mPlayer.getKeyStatuses();
    } catch (error: unknown) {
      this.log("warn", "getKeyStatuses()", error);
      return {};
    }
  }

  // ── Configuration (Shaka-specific) ─────────────────────────────────────────

  public configure(config: Record<string, unknown>): void {
    if (this.mPlayer === null) {
      this.log("warn", "configure(): Player not initialized.");
      return;
    }
    this.mPlayer.configure(config);
  }

  public resetConfiguration(): void {
    if (this.mPlayer === null) {
      this.log("warn", "resetConfiguration(): Player not initialized.");
      return;
    }
    this.mPlayer.resetConfiguration();
    this.applyBaseConfiguration();
    this.log(
      "debug",
      "resetConfiguration(): Configuration reset and base config reapplied"
    );
  }

  /**
   * Apply SHAKA_PLAYER_CONFIG plus the parts of the base configuration that
   * need this instance, so init() and resetConfiguration() stay in step.
   */
  private applyBaseConfiguration(): void {
    if (this.mPlayer === null) {
      return;
    }
    this.mPlayer.configure(SHAKA_PLAYER_CONFIG);
    this.mPlayer.configure(
      "streaming.failureCallback",
      (error: shaka.util.Error): void => {
        this.onStreamingFailure(error);
      }
    );
  }

  public overrideAvailabilityWindowForLinear(): void {
    if (this.mPlayer === null) {
      this.log(
        "warn",
        "overrideAvailabilityWindowForLinear(): Player not initialized."
      );
      return;
    }
    const seekRange = this.mPlayer.seekRange();
    const availabilityWindow = seekRange.end - seekRange.start;

    if (
      !this.applyManifestConfig(
        { availabilityWindowOverride: availabilityWindow },
        "overrideAvailabilityWindowForLinear"
      )
    ) {
      return;
    }

    this.log(
      "debug",
      "overrideAvailabilityWindowForLinear(): Window set to " +
        String(availabilityWindow)
    );
  }

  /**
   * Applies a manifest configuration change to the loaded stream.
   *
   * Unlike the reset path, these calls have to configure *while* a manifest
   * is loaded, so Shaka re-filters its variants and can throw on a manifest
   * carrying a variant with no audio or no video track. The failure is
   * contained here because the callers run inside player-event handlers:
   * letting it escape aborts the rest of the handler — first-frame setup
   * included — over what is only a seek-window refinement.
   *
   * @returns Whether the configuration was applied.
   */
  private applyManifestConfig(
    manifest: Record<string, unknown>,
    caller: string
  ): boolean {
    if (this.mPlayer === null) {
      return false;
    }

    try {
      this.mPlayer.configure({ manifest });
      return true;
    } catch (error: unknown) {
      this.log("error", caller + "(): configure failed", error);
      return false;
    }
  }

  public overrideAvailabilityWindowForStartOver(): void {
    if (this.mPlayer === null) {
      this.log(
        "warn",
        "overrideAvailabilityWindowForStartOver(): Player not initialized."
      );
      return;
    }
    if (
      !this.applyManifestConfig(
        { availabilityWindowOverride: Infinity },
        "overrideAvailabilityWindowForStartOver"
      )
    ) {
      return;
    }

    this.log(
      "debug",
      "overrideAvailabilityWindowForStartOver(): Window set to Infinity"
    );
  }

  // ── Statistics ──────────────────────────────────────────────────────────────

  public getStats(): TPlayerStats {
    if (this.mPlayer === null) {
      return this.buildEmptyStats();
    }
    const s = this.mPlayer.getStats();
    return {
      bufferingTime: s.bufferingTime,
      bytesDownloaded: s.bytesDownloaded,
      completionPercent: s.completionPercent,
      corruptedFrames: s.corruptedFrames,
      currentCodecs: s.currentCodecs,
      decodedFrames: s.decodedFrames,
      droppedFrames: s.droppedFrames,
      drmTimeSeconds: s.drmTimeSeconds,
      estimatedBandwidth: s.estimatedBandwidth,
      gapsJumped: s.gapsJumped,
      height: s.height,
      licenseTime: s.licenseTime,
      liveLatency: s.liveLatency,
      loadLatency: s.loadLatency,
      manifestGapCount: s.manifestGapCount,
      manifestPeriodCount: s.manifestPeriodCount,
      manifestSizeBytes: s.manifestSizeBytes,
      manifestTimeSeconds: s.manifestTimeSeconds,
      maxSegmentDuration: s.maxSegmentDuration,
      nonFatalErrorCount: s.nonFatalErrorCount,
      pauseTime: s.pauseTime,
      playTime: s.playTime,
      stallsDetected: s.stallsDetected,
      stateHistory: s.stateHistory.map((entry) => ({
        timestamp: entry.timestamp,
        state: entry.state,
        duration: entry.duration
      })),
      streamBandwidth: s.streamBandwidth,
      switchHistory: s.switchHistory.map((entry) => ({
        timestamp: entry.timestamp,
        id: entry.id,
        type: entry.type as "variant" | "text",
        fromAdaptation: entry.fromAdaptation,
        bandwidth: entry.bandwidth
      })),
      width: s.width
    };
  }

  public getAssetUri(): string | null {
    if (this.mPlayer === null) {
      return null;
    }
    try {
      return this.mPlayer.getAssetUri();
    } catch (error: unknown) {
      this.log("warn", "getAssetUri()", error);
      return null;
    }
  }

  // ── Events ─────────────────────────────────────────────────────────────────

  public on<K extends keyof TPlayerEventMap>(
    eventType: K,
    listener: (event: TPlayerEventMap[K]) => void
  ): void {
    if (!this.mListeners.has(eventType)) {
      this.mListeners.set(eventType, new Set());
    }
    const listeners = this.mListeners.get(eventType);
    if (listeners !== undefined) {
      listeners.add(listener);
    }
  }

  public off<K extends keyof TPlayerEventMap>(
    eventType: K,
    listener: (event: TPlayerEventMap[K]) => void
  ): void {
    const listeners = this.mListeners.get(eventType);
    if (listeners !== undefined) {
      listeners.delete(listener);
    }
  }

  // ── Private: Helper Functions ───────────────────────────────────────────────

  /**
   * Clamps a **player-clock** position into the seekable range.
   *
   * Deliberately reads the raw range: every caller is about to write the
   * result to `mVideoEl.currentTime`, so clamping against the normalized
   * range would compare a position to bounds from a different clock.
   */
  private clampToSeekableRange(positionSeconds: number): number {
    const range = this.getRawSeekableRange();
    return Math.max(range.start, Math.min(range.end, positionSeconds));
  }

  // ── Private: State Machine ─────────────────────────────────────────────────

  private transitionState(newState: PlayerState): void {
    if (this.mState === newState) {
      return;
    }
    const previousState = this.mState;
    this.mState = newState;
    this.emitEvent(PlayerState.STATE_CHANGED, {
      timestamp: Date.now(),
      newState,
      previousState
    });
  }

  private emitEvent<K extends keyof TPlayerEventMap>(
    eventType: K,
    payload: TPlayerEventMap[K]
  ): void {
    const listeners = this.mListeners.get(eventType);
    if (listeners === undefined || listeners.size === 0) {
      return;
    }
    listeners.forEach((listener) => {
      try {
        (listener as (event: TPlayerEventMap[K]) => void)(payload);
      } catch (error: unknown) {
        this.log(
          "error",
          `emitEvent(): Listener threw for event "${eventType}"`,
          error
        );
      }
    });
  }

  // ── Private: Error Recovery ────────────────────────────────────────────────

  /**
   * Retain the source config for a possible replay and, for genuine
   * user-initiated loads, reset the retry budget. A recovery replay
   * (mIsRetrying) preserves the in-progress budget.
   */
  private prepareRetryStateForLoad(config: TPlayerSourceConfig): void {
    this.mLastSourceConfig = config;
    if (!this.mIsRetrying) {
      this.mRetryCount = 0;
      this.clearRetryTimer();
      this.clearNetworkErrorDeferral();
    }
  }

  /**
   * Decide how to handle a Shaka error: surface it as a terminal error,
   * escalate to a fatal error once the recovery budget is spent, or schedule
   * an automatic recovery replay.
   */
  private handlePlaybackError(
    shakaError: {
      severity?: number;
      category?: number;
      code?: number;
      data?: unknown[];
      message?: string;
    },
    playerError: TPlayerError
  ): void {
    this.log(
      "warn",
      `handlePlaybackError(): ${playerError.category} ${playerError.code}`,
      shakaError
    );

    // Whatever this error leads to — a terminal failure or a replay — makes any
    // network error still being withheld moot.
    this.clearNetworkErrorDeferral();

    if (!ShakaPlayerUtils.isRetryableError(shakaError)) {
      // Non-recoverable: surface immediately as a terminal error.
      this.transitionState(PlayerState.ERROR);
      this.emitEvent(PlayerState.ERROR, {
        timestamp: Date.now(),
        error: playerError
      });
      // A RECOVERABLE one is Shaka saying it is still working on it, so only a
      // CRITICAL one ends playback.
      if (playerError.severity === PlayerErrorSeverity.CRITICAL) {
        this.stopStreaming();
      }
      return;
    }

    if (this.mRetryCount >= ShakaVideoPlayerImpl.RETRY_LIMIT) {
      this.emitFatalError(playerError);
      return;
    }

    // Recoverable and budget remaining: signal recovery and schedule a replay.
    this.startRecovery(playerError);
  }

  /**
   * Recovery budget exhausted: escalate to a fatal, critical error.
   */
  private emitFatalError(playerError: TPlayerError): void {
    this.clearRetryTimer();
    this.mIsRetrying = false;
    const fatalError: TPlayerError = {
      ...playerError,
      severity: PlayerErrorSeverity.CRITICAL
    };
    this.transitionState(PlayerState.ERROR);
    this.emitEvent(PlayerState.FATAL_ERROR, {
      timestamp: Date.now(),
      error: fatalError
    });
    // Also emit ERROR so existing subscribers — which do not yet listen for
    // FATAL_ERROR — still observe the terminal failure.
    this.emitEvent(PlayerState.ERROR, {
      timestamp: Date.now(),
      error: fatalError
    });
    this.stopStreaming();
  }

  /**
   * Stop every segment and manifest request once playback has failed for
   * good. Entering ERROR is only a state change: on a live (dynamic) manifest
   * Shaka's StreamingEngine keeps refetching segments and refreshing the
   * manifest behind the error dialog indefinitely, and pausing the element
   * does not stop it. Unloading the Shaka source is the only thing that does.
   *
   * Calls Shaka directly rather than this.unload(): the player stays in ERROR
   * (not IDLE) for the error UI, and the retained source is kept. The next
   * load() — a re-tune or restorePlayback() — unloads through the normal path
   * from ERROR and resets the configuration as usual.
   */
  private stopStreaming(): void {
    if (this.mPlayer === null) {
      return;
    }
    // Nothing more can arrive to act on, so stop watching the buffer.
    this.clearNetworkErrorDeferral();
    this.log("info", "stopStreaming(): unloading source after terminal error");
    this.mPlayer.unload().catch((error: unknown) => {
      this.log("error", "stopStreaming(): unload failed", error);
    });
  }

  /**
   * Replaces Shaka's default `streaming.failureCallback`, which on a live
   * stream answers every BAD_HTTP_STATUS / HTTP_ERROR / TIMEOUT with
   * `retryStreaming()` after a flat 0.1s (lowLatencyMode) — forever. During
   * an outage that refetches the failing segments several times a second for
   * as long as the outage lasts. Same retry decision, but backed off
   * exponentially, resetting once a segment downloads again.
   *
   * VOD keeps Shaka's behavior — a streaming failure there is final.
   */
  private onStreamingFailure(error: shaka.util.Error): void {
    if (
      this.mPlayer === null ||
      !this.mPlayer.isLive() ||
      !ShakaPlayerUtils.isLiveStreamingRetryError(error)
    ) {
      return;
    }

    const delaySeconds = Math.min(
      ShakaVideoPlayerImpl.LIVE_STREAMING_RETRY_BASE_DELAY_S *
        2 ** this.mLiveStreamingFailureCount,
      ShakaVideoPlayerImpl.LIVE_STREAMING_RETRY_MAX_DELAY_S
    );
    this.mLiveStreamingFailureCount += 1;
    // Mirrors Shaka's default: a failure it is about to retry is recoverable.
    error.severity = shaka.util.Error.Severity.RECOVERABLE;
    this.log(
      "warn",
      `onStreamingFailure(): live streaming error ${error.code}, retrying in ${delaySeconds}s`
    );
    this.mPlayer.retryStreaming(delaySeconds);
  }

  /**
   * Spend one attempt from the recovery budget: signal recovery and schedule
   * the replay.
   */
  private startRecovery(playerError: TPlayerError): void {
    this.mRetryCount += 1;
    // Enter ERROR_RECOVERY as a real state, not just an event: the state change
    // is what propagates to the UI (STATE_CHANGED → VideoManager → controller →
    // playerState) and puts the loading indicator up for the duration of the
    // replay, instead of leaving the viewer on a frozen frame.
    this.transitionState(PlayerState.ERROR_RECOVERY);
    this.emitEvent(PlayerState.ERROR_RECOVERY, {
      timestamp: Date.now(),
      code: playerError.code
    });
    this.scheduleRetry(playerError);
  }

  // ── Private: Deferred Network Errors ───────────────────────────────────────

  /**
   * Entry point for every network error. A network failure is always retried
   * before it is allowed to surface — the only question is *when* the retry
   * starts:
   *
   * - **Playback running**: the failure is not user-visible until the buffer
   *   runs dry. SHAKA_PLAYER_CONFIG buffers up to `bufferingGoal` (18s) ahead,
   *   and Shaka has its own recovery — it disables the failing variant and
   *   switches, and for live streams retries streaming — which usually succeeds
   *   inside that window. So the error is withheld and the buffer watched;
   *   recovery only starts once the buffer is spent. Mirrors kaveri's
   *   `isErrorBufferEnded` window of 50s, or 30s once a retry has been spent.
   * - **Nothing rendered yet**: there is no buffered content for the outage to
   *   hide behind, so waiting would only delay the recovery. Retry at once.
   *
   * Either way the recovery budget bounds it: once RETRY_LIMIT attempts are
   * spent the failure is escalated to a terminal player error.
   *
   * @param playerError - The normalized network error.
   */
  private handleNetworkError(playerError: TPlayerError): void {
    // Already withholding an error for this outage: keep the original deadline
    // and just retain the latest one. Shaka re-fires the same network error for
    // every failed segment.
    if (this.mDeferredNetworkError !== null) {
      this.mDeferredNetworkError = playerError;
      return;
    }

    if (!this.mFirstFrameFired) {
      this.log(
        "warn",
        `handleNetworkError(): no buffer to play out, retrying ${playerError.code} now`
      );
      this.retryOrEscalate(playerError);
      return;
    }

    const graceMs =
      this.mRetryCount > 0
        ? ShakaVideoPlayerImpl.NETWORK_ERROR_GRACE_RETRY_MS
        : ShakaVideoPlayerImpl.NETWORK_ERROR_GRACE_MS;

    this.log(
      "warn",
      `handleNetworkError(): withholding ${playerError.code} for up to ${graceMs}ms`
    );

    this.mDeferredNetworkError = playerError;
    this.mNetworkErrorDeadlineMs = Date.now() + graceMs;
    this.clearNetworkErrorPollTimer();
    this.mNetworkErrorPollTimerId = setInterval(() => {
      this.checkDeferredNetworkError();
    }, ShakaVideoPlayerImpl.NETWORK_ERROR_POLL_MS);
  }

  /**
   * Poll body for a withheld network error. Three outcomes: the buffer drained
   * (the outage is now visible, so act on it), the grace window elapsed with
   * the buffer still healthy (Shaka recovered by itself — discard it), or
   * neither yet (keep waiting).
   */
  private checkDeferredNetworkError(): void {
    const playerError = this.mDeferredNetworkError;
    if (playerError === null) {
      this.clearNetworkErrorPollTimer();
      return;
    }

    if (this.isPlaybackBufferDrained()) {
      this.log(
        "warn",
        `checkDeferredNetworkError(): buffer drained, acting on ${playerError.code}`
      );
      this.clearNetworkErrorDeferral();
      this.retryOrEscalate(playerError);
      return;
    }

    if (Date.now() >= this.mNetworkErrorDeadlineMs) {
      // The window elapsed with the buffer still healthy, so Shaka recovered
      // on its own and the viewer never saw a glitch — there is nothing worth
      // reporting. kaveri replays here instead, interrupting playback that is
      // already fine.
      this.log(
        "info",
        `checkDeferredNetworkError(): discarding recovered ${playerError.code}`
      );
      this.clearNetworkErrorDeferral();
    }
  }

  /**
   * Route a failed load() into the retry ladder.
   *
   * A rejected load() is the only signal that the source could not be opened:
   * Shaka unloads and rethrows without dispatching an error event, and callers
   * only log the rejection. Without this the failure never reaches the viewer.
   *
   * A recovery replay's own reload is excluded — replayFromSavedPosition()
   * already accounts for that attempt, and handling it in both places would
   * spend two budget slots per failure.
   */
  private handleLoadFailure(failure: unknown): void {
    this.log("error", "load()", failure);
    if (this.mIsRetrying) {
      return;
    }
    this.retryOrEscalate(ShakaVideoPlayerImpl.toEnrichedPlayerError(failure));
  }

  /**
   * Normalize a Shaka error — or any thrown load failure — into a
   * {@link TPlayerError} carrying the human-readable category and the compound
   * application code (e.g. `"V641-002"`) that the downstream error mapper
   * routes on. The base code comes from {@link getWebErrorCode}, which maps
   * the exact Shaka error code (disambiguated by fetch stage or native
   * MediaError where the doc requires it) to the same V-code Android reports
   * for the equivalent failure, defaulting to `V555` when unlisted. A value
   * that is not an object normalizes to `V555-000`.
   */
  private static toEnrichedPlayerError(error: unknown): TPlayerError {
    const shakaError: {
      severity?: number;
      category?: number;
      code?: number;
      data?: unknown[];
      message?: string;
    } =
      typeof error === "object" && error !== null
        ? (error as { severity?: number; category?: number; code?: number })
        : {};

    const playerError = ShakaPlayerUtils.toPlayerError(shakaError);
    playerError.category = ShakaPlayerUtils.getCategoryName(
      shakaError.category
    );

    const numericCode =
      typeof shakaError.code === "number"
        ? shakaError.code
        : Number(shakaError.code);
    const requestType = ShakaPlayerUtils.getRequestType(shakaError);
    const mediaErrorCode = ShakaPlayerUtils.getMediaErrorCode(shakaError);
    playerError.code =
      getWebErrorCode(numericCode, requestType, mediaErrorCode) +
      PLAYER_ERROR_SEPARATOR +
      ShakaPlayerUtils.getSubErrorCode(shakaError);
    return playerError;
  }

  /**
   * Spend one more attempt on a failure, or escalate to a fatal error once the
   * budget is gone. Mirrors kaveri's `handleShakaNetworkError`.
   *
   * Called for a network error that can no longer be hidden behind the buffer,
   * and for a recovery replay whose own reload failed. The former deliberately
   * bypasses {@link ShakaPlayerUtils.isRetryableError}, which classifies
   * NETWORK as non-retryable: network failures *are* retried, just not while
   * buffered content is still hiding the outage.
   */
  private retryOrEscalate(playerError: TPlayerError): void {
    if (this.mRetryCount >= ShakaVideoPlayerImpl.RETRY_LIMIT) {
      this.emitFatalError(playerError);
      return;
    }
    this.startRecovery(playerError);
  }

  /**
   * True when there is less than {@link NETWORK_ERROR_BUFFER_FLOOR_SECONDS}
   * left ahead of the playhead, or Shaka has already reported that it cannot
   * keep playing.
   */
  private isPlaybackBufferDrained(): boolean {
    if (this.mVideoEl === null) {
      return true;
    }

    // A paused player is not consuming its buffer, so the outage cannot become
    // visible while it stays paused — and acting would resume playback the
    // viewer deliberately stopped. kaveri guards the same case by refusing to
    // handle a network error while the player is paused.
    if (this.mVideoEl.paused) {
      return false;
    }

    // Shaka only reports buffering once the buffer falls under
    // streaming.rebufferingGoal, so this is the same condition observed from
    // the player rather than from the ranges.
    if (this.mState === PlayerState.BUFFERING) {
      return true;
    }

    const currentTime = this.getCurrentTime();
    const buffered = this.getBufferedRanges();
    for (let i = 0; i < buffered.length; i += 1) {
      if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
        return (
          buffered.end(i) - currentTime <
          ShakaVideoPlayerImpl.NETWORK_ERROR_BUFFER_FLOOR_SECONDS
        );
      }
    }

    // The playhead sits outside every buffered range — nothing left to play.
    return true;
  }

  /** Cancel the buffer-watch timer for a deferred network error. */
  private clearNetworkErrorPollTimer(): void {
    if (this.mNetworkErrorPollTimerId !== null) {
      clearInterval(this.mNetworkErrorPollTimerId);
      this.mNetworkErrorPollTimerId = null;
    }
  }

  /** Drop any withheld network error and stop watching the buffer. */
  private clearNetworkErrorDeferral(): void {
    this.clearNetworkErrorPollTimer();
    this.mDeferredNetworkError = null;
    this.mNetworkErrorDeadlineMs = 0;
  }

  /**
   * Capture the current position and schedule a replay after a short delay.
   * Falls back to a terminal error if there is no retained source to replay.
   */
  private scheduleRetry(playerError: TPlayerError): void {
    if (
      this.mLastSourceConfig === null ||
      this.mVideoEl === null ||
      this.mPlayer === null
    ) {
      this.transitionState(PlayerState.ERROR);
      this.emitEvent(PlayerState.ERROR, {
        timestamp: Date.now(),
        error: playerError
      });
      this.stopStreaming();
      return;
    }

    // Keep the last known-good position when the element has already been torn
    // down by a failed load — currentTime reads 0 there, and overwriting would
    // restart the stream from the beginning on every attempt after the first.
    const currentTime = this.getCurrentTime();
    if (currentTime > 0) {
      this.mRetrySavedPosition = currentTime;
    }
    this.clearRetryTimer();
    this.mRetryTimerId = setTimeout(() => {
      this.mRetryTimerId = null;
      this.replayFromSavedPosition(playerError).catch((error: unknown) => {
        this.log("error", "scheduleRetry(): replay failed", error);
      });
    }, ShakaVideoPlayerImpl.RETRY_DELAY_MS);
  }

  /**
   * Reload the retained source to recover from a recoverable error. VOD
   * resumes at the captured position; live resumes at the live edge.
   *
   * @param playerError - The failure this replay is recovering from, needed to
   *   re-enter the retry ladder if the reload itself fails.
   */
  private async replayFromSavedPosition(
    playerError: TPlayerError
  ): Promise<void> {
    const config = this.mLastSourceConfig;
    if (config === null || this.mPlayer === null) {
      return;
    }

    const replayConfig: TPlayerSourceConfig = {
      ...config,
      playbackConfig: {
        ...config.playbackConfig,
        autoplay: true,
        startPosition: this.isLive() ? undefined : this.mRetrySavedPosition
      }
    };

    this.mIsRetrying = true;
    const recoveryGeneration: number = this.mRecoveryGeneration;
    const isSuperseded = (): boolean =>
      recoveryGeneration !== this.mRecoveryGeneration;
    let reloadFailed = false;
    try {
      await this.loadSource(replayConfig);
      if (isSuperseded()) {
        return;
      }
      this.play();
    } catch (error: unknown) {
      this.log("error", "replayFromSavedPosition(): reload failed", error);
      reloadFailed = true;
    } finally {
      // A superseding load or unload already cleared the flag, and a newer
      // replay may have set it again since.
      if (!isSuperseded()) {
        this.mIsRetrying = false;
      }
    }

    if (reloadFailed && !isSuperseded()) {
      // A rejected load() is the only signal that the reload failed: Shaka
      // unloads and rethrows without dispatching an error event, so no further
      // event will arrive. Feeding it back into the ladder is what keeps a
      // source that cannot be reloaded from stalling silently, never spending
      // the budget and never surfacing an error.
      this.retryOrEscalate(playerError);
    }
  }

  /** Cancel any pending recovery-reload timer. */
  private clearRetryTimer(): void {
    if (this.mRetryTimerId !== null) {
      clearTimeout(this.mRetryTimerId);
      this.mRetryTimerId = null;
    }
  }

  // ── Private: Shaka Event Bridge ────────────────────────────────────────────

  private registerShakaEventListeners(): void {
    if (this.mPlayer === null) {
      return;
    }

    this.addShakaListener(PlayerState.LOADING, (_event) => {
      this.transitionState(PlayerState.LOADING);
      this.emitEvent(PlayerState.LOADING, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.LOADED, (_event) => {
      this.transitionState(PlayerState.READY);
      this.emitEvent(PlayerState.LOADED, { timestamp: Date.now() });
    });

    this.addVideoListener(PlayerState.PLAYING, (_event) => {
      this.transitionState(PlayerState.PLAYING);
      this.emitEvent(PlayerState.PLAYING, { timestamp: Date.now() });
      // Playback resumed successfully — clear any in-flight recovery state and
      // restore a fresh retry budget for the next independent failure.
      this.mRetryCount = 0;
      this.mIsRetrying = false;
      this.clearRetryTimer();
      // Playback is running again, so a withheld network error described a
      // condition that has passed. kaveri never clears its equivalent, so its
      // grace window only ever applies to the first failure of a session.
      this.clearNetworkErrorDeferral();
    });

    this.addVideoListener(PlayerState.PAUSE, (_event) => {
      if (this.mState !== PlayerState.ENDED) {
        this.transitionState(PlayerState.PAUSED);
      }
      this.emitEvent(PlayerState.PAUSE, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.BUFFERING, (event) => {
      // Shaka's FakeEvent puts properties directly on the event object.
      const shakaEvent = event as Event & { buffering?: boolean };
      const isBuffering = shakaEvent.buffering === true;

      if (isBuffering) {
        this.transitionState(PlayerState.BUFFERING);
      } else if (
        this.mState === PlayerState.BUFFERING &&
        this.mVideoEl !== null &&
        !this.mVideoEl.paused
      ) {
        this.transitionState(PlayerState.PLAYING);
      }

      this.emitEvent(PlayerState.BUFFERING, {
        timestamp: Date.now(),
        isBuffering
      });
    });

    this.addVideoListener(PlayerState.SEEKING, (_event) => {
      this.mPreSeekState = this.mState;

      // Native seeks (e.g. the browser's own PiP window scrubber) write
      // directly to currentTime, bypassing seek()'s clamp — re-clamp here so
      // the play position can never move past the live edge.
      if (this.mVideoEl !== null) {
        const clamped = this.clampToSeekableRange(this.mVideoEl.currentTime);
        if (clamped !== this.mVideoEl.currentTime) {
          this.mVideoEl.currentTime = clamped;
        }
      }

      this.transitionState(PlayerState.SEEKING);
      this.emitEvent(PlayerState.SEEKING, {
        timestamp: Date.now(),
        targetPosition: this.getCurrentTime()
      });
    });

    this.addVideoListener(PlayerState.SEEKED, (_event) => {
      if (this.mState === PlayerState.SEEKING) {
        const resumeState =
          this.mPreSeekState === PlayerState.PAUSED ||
          (this.mVideoEl !== null && this.mVideoEl.paused)
            ? PlayerState.PAUSED
            : PlayerState.PLAYING;
        this.transitionState(resumeState);
      }
      this.emitEvent(PlayerState.SEEKED, {
        timestamp: Date.now(),
        targetPosition: this.getCurrentTime()
      });
    });

    this.addVideoListener(PlayerState.ENDED, (_event) => {
      this.transitionState(PlayerState.ENDED);
      this.emitEvent(PlayerState.ENDED, {
        timestamp: Date.now(),
        duration: this.mVideoEl !== null ? this.mVideoEl.duration : 0
      });
    });

    this.addShakaListener(PlayerState.ERROR, (event) => {
      // Shaka's FakeEvent carries the shaka.util.Error payload in event.detail.
      const shakaEvent = event as Event & {
        detail?: {
          severity?: number;
          category?: number;
          code?: number;
          data?: unknown[];
          message?: string;
        };
      };
      const shakaError = shakaEvent.detail;
      if (shakaError === null || shakaError === undefined) {
        this.log("warn", "PlayerState.ERROR received without event.detail");
        return;
      }

      const normalizedShakaError: {
        severity?: number;
        category?: number;
        code?: number;
        data?: unknown[];
        message?: string;
      } = shakaError;

      // LOAD_INTERRUPTED (7000) fires when load() is preempted by a subsequent
      // load() or unload(). It is not a real failure; ignore it.
      if (!ShakaPlayerUtils.isErrorHandlingRequired(normalizedShakaError)) {
        return;
      }

      this.log("error", "Shaka error:", normalizedShakaError);

      const playerError =
        ShakaVideoPlayerImpl.toEnrichedPlayerError(normalizedShakaError);
      const isDrmEnforcement =
        ShakaPlayerUtils.getHttpErrorCode(normalizedShakaError) !== "";

      // DRM enforcement errors (HTTP 401/409 from the license server) are
      // handled exclusively by the forced-SDT-refresh path in onDrmFail().
      // Do NOT fall through to handlePlaybackError — it would immediately
      // put the player into ERROR state and race with the async recovery.
      if (playerError.category === "DRM" && isDrmEnforcement) {
        this.emitEvent(PlayerState.DRM_FAILED, { timestamp: Date.now() });
        return;
      }

      // Network errors have their own recovery path: they are always retried
      // first, and only become a terminal error once the budget is spent. The
      // generic classifier would treat them as non-retryable and fail outright.
      if (ShakaPlayerUtils.isShakaNetworkError(normalizedShakaError)) {
        this.handleNetworkError(playerError);
        return;
      }

      this.handlePlaybackError(normalizedShakaError, playerError);
    });

    this.addVideoListener(PlayerState.TIME_UPDATE, (_event) => {
      if (this.mVideoEl === null) {
        return;
      }

      // Two clocks in play here, deliberately. The raw position is what the
      // checks below mean — "has playback started", "is it near the end of
      // the media" — and those readings would be nonsense against an epoch
      // value. Everything reported outward uses the normalized position, so
      // it agrees with the seekable range and with what callers read back.
      // Read once and shared, so the position and the range reported in one
      // payload are anchored to the same instant. The offset tracks a moving
      // playhead, so deriving it twice would let the two disagree by however
      // long the handler took to run.
      const offset = this.getTimebaseOffsetSeconds();
      const rawRange = this.getRawSeekableRange();
      const rawCurrentTime = this.mVideoEl.currentTime;
      const currentTime = rawCurrentTime + offset;
      const duration = this.mVideoEl.duration;
      const seekableRange: TSeekRange = {
        start: rawRange.start + offset,
        end: rawRange.end + offset,
        duration: rawRange.duration
      };

      // ── Custom first-frame detection ──
      // Neither Shaka Player nor the HTMLVideoElement provide a native
      // "first frame rendered" event. We detect it by observing the first
      // timeupdate where currentTime > 0 after a load().
      if (!this.mFirstFrameFired && rawCurrentTime > 0) {
        this.mFirstFrameFired = true;
        this.emitEvent(PlayerState.FIRST_FRAME, {
          timestamp: Date.now(),
          seekRangeStart: seekableRange.start
        });
      }

      // ── VOD EOS threshold detection ──
      // The native 'ended' event can be unreliable on some Smart TV
      // browsers. We also fire ENDED when currentTime is within the
      // threshold of the duration for non-live, finite-duration content.
      if (
        this.mState === PlayerState.PLAYING &&
        !this.isLive() &&
        isFinite(duration) &&
        duration > 0 &&
        rawCurrentTime >= duration - ShakaVideoPlayerImpl.EOS_THRESHOLD_SECONDS
      ) {
        this.transitionState(PlayerState.ENDED);
        this.emitEvent(PlayerState.ENDED, {
          timestamp: Date.now(),
          duration
        });
      }

      // ── Trick-play boundary detection ──
      // When trick-playing (rate ≠ 1), automatically exit trick-play mode
      // and reset to normal speed if playback reaches near the start or
      // end of the seekable range (rolling buffer boundaries).
      const playbackRate = this.mVideoEl.playbackRate;
      if (
        playbackRate !== 1 &&
        seekableRange.duration > 0 &&
        this.mPlayer !== null
      ) {
        const threshold = ShakaVideoPlayerImpl.TRICK_PLAY_BOUNDARY_THRESHOLD;
        const nearStart = currentTime <= seekableRange.start + threshold;
        const nearEnd = currentTime >= seekableRange.end - threshold;

        if ((playbackRate < 0 && nearStart) || (playbackRate > 0 && nearEnd)) {
          this.log(
            "debug",
            "Trick-play boundary reached (rate=" +
              playbackRate +
              ", time=" +
              currentTime.toFixed(2) +
              ", range=" +
              seekableRange.start.toFixed(2) +
              "-" +
              seekableRange.end.toFixed(2) +
              "). Resetting to 1×."
          );
          this.setTrickPlayRate(1);
        }
      }

      this.emitEvent(PlayerState.TIME_UPDATE, {
        timestamp: Date.now(),
        currentTime,
        duration,
        seekableRange
      });
    });

    this.addShakaListener(PlayerState.UNLOADING, (_event) => {
      this.emitEvent(PlayerState.UNLOADING, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.STARTED, (_event) => {
      this.emitEvent(PlayerState.STARTED, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.STREAMING, (_event) => {
      this.emitEvent(PlayerState.STREAMING, { timestamp: Date.now() });
    });

    this.addVideoListener(PlayerState.STALLED, (_event) => {
      this.emitEvent(PlayerState.STALLED, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.MANIFEST_PARSED, (_event) => {
      this.emitEvent(PlayerState.MANIFEST_PARSED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.MANIFEST_UPDATED, (_event) => {
      this.emitEvent(PlayerState.MANIFEST_UPDATED, {
        timestamp: Date.now()
      });

      // ── Dynamic-to-static transition (live → VOD) ──
      // When a live stream ends, the manifest transitions from dynamic to
      // static. We detect this by checking if the stream was live at load
      // time but is no longer live. On detection we restart playback at
      // the last known position so the player can reach the new end-of-stream.
      this.detectDynamicToStaticTransition();
    });

    this.addShakaListener(PlayerState.TRACKS_CHANGED, (_event) => {
      this.emitEvent(PlayerState.TRACKS_CHANGED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.TEXT_CHANGED, (_event) => {
      this.emitEvent(PlayerState.TEXT_CHANGED, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.ADAPTATION, (event) => {
      // Shaka's FakeEvent puts track properties directly on the event.
      const shakaEvent = event as Event & {
        newTrack?: shaka.extern.Track;
        oldTrack?: shaka.extern.Track;
      };
      this.emitEvent(PlayerState.ADAPTATION, {
        timestamp: Date.now(),
        newTrack:
          shakaEvent.newTrack !== undefined
            ? this.mapShakaTrackToVariantTrack(shakaEvent.newTrack)
            : null,
        oldTrack:
          shakaEvent.oldTrack !== undefined
            ? this.mapShakaTrackToVariantTrack(shakaEvent.oldTrack)
            : null
      });
    });

    this.addShakaListener(PlayerState.VARIANT_CHANGED, (event) => {
      const shakaEvent = event as Event & {
        newTrack?: shaka.extern.Track;
      };
      const newTrack =
        shakaEvent.newTrack !== undefined
          ? this.mapShakaTrackToVariantTrack(shakaEvent.newTrack)
          : null;
      this.emitEvent(PlayerState.VARIANT_CHANGED, {
        timestamp: Date.now(),
        newTrack
      });
    });

    this.addShakaListener(PlayerState.ABR_STATUS_CHANGED, (event) => {
      const shakaEvent = event as Event & { newStatus?: boolean };
      this.emitEvent(PlayerState.ABR_STATUS_CHANGED, {
        timestamp: Date.now(),
        newStatus:
          shakaEvent.newStatus !== undefined ? shakaEvent.newStatus : false
      });
    });

    this.addVideoListener(PlayerState.RATE_CHANGE, (_event) => {
      this.emitEvent(PlayerState.RATE_CHANGE, {
        timestamp: Date.now(),
        rate: this.mVideoEl !== null ? this.mVideoEl.playbackRate : 1
      });
    });

    this.addShakaListener(PlayerState.DRM_SESSION_UPDATE, (_event) => {
      this.emitEvent(PlayerState.DRM_SESSION_UPDATE, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.EXPIRATION_UPDATED, (_event) => {
      this.emitEvent(PlayerState.EXPIRATION_UPDATED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.KEY_STATUS_CHANGED, (_event) => {
      this.emitEvent(PlayerState.KEY_STATUS_CHANGED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.DOWNLOAD_FAILED, (event) => {
      this.emitEvent(PlayerState.DOWNLOAD_FAILED, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.DOWNLOAD_HEADERS_RECEIVED, (event) => {
      this.emitEvent(PlayerState.DOWNLOAD_HEADERS_RECEIVED, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.SEGMENT_APPENDED, (_event) => {
      this.emitEvent(PlayerState.SEGMENT_APPENDED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.EMSG, (event) => {
      this.emitEvent(PlayerState.EMSG, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.GAP_JUMPED, (_event) => {
      this.emitEvent(PlayerState.GAP_JUMPED, { timestamp: Date.now() });
    });

    this.addShakaListener(PlayerState.STALL_DETECTED, (_event) => {
      this.emitEvent(PlayerState.STALL_DETECTED, {
        timestamp: Date.now()
      });
    });

    this.addShakaListener(PlayerState.TIMELINE_REGION_ADDED, (event) => {
      this.emitEvent(PlayerState.TIMELINE_REGION_ADDED, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.TIMELINE_REGION_ENTER, (event) => {
      this.emitEvent(PlayerState.TIMELINE_REGION_ENTER, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.TIMELINE_REGION_EXIT, (event) => {
      this.emitEvent(PlayerState.TIMELINE_REGION_EXIT, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.PRFT, (event) => {
      this.emitEvent(PlayerState.PRFT, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.SESSION_DATA, (event) => {
      this.emitEvent(PlayerState.SESSION_DATA, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.SPATIAL_VIDEO_INFO, (event) => {
      this.emitEvent(PlayerState.SPATIAL_VIDEO_INFO, {
        timestamp: Date.now(),
        detail: event
      });
    });

    this.addShakaListener(PlayerState.MEDIA_QUALITY_CHANGED, (event) => {
      const shakaEvent = event as Event & {
        mediaQuality?: unknown;
        position?: number;
      };
      this.emitEvent(PlayerState.MEDIA_QUALITY_CHANGED, {
        timestamp: Date.now(),
        mediaQuality: shakaEvent.mediaQuality,
        position: shakaEvent.position !== undefined ? shakaEvent.position : 0
      });
    });

    this.addShakaListener(PlayerState.COMPLETE, (_event) => {
      this.emitEvent(PlayerState.COMPLETE, { timestamp: Date.now() });
    });
  }

  private addShakaListener(
    playerState: PlayerState,
    handler: (event: Event) => void
  ): void {
    if (this.mPlayer === null) {
      return;
    }
    const eventName = SHAKA_EVENT_NAME[playerState];
    if (eventName === undefined) {
      this.log(
        "warn",
        "addShakaListener(): No Shaka event name for " + playerState
      );
      return;
    }
    this.mShakaEventListeners.set(eventName, handler);
    this.mPlayer.addEventListener(eventName, handler);
  }

  private addVideoListener(
    playerState: PlayerState,
    handler: (event: Event) => void
  ): void {
    if (this.mVideoEl === null) {
      return;
    }
    const eventName = SHAKA_EVENT_NAME[playerState];
    if (eventName === undefined) {
      this.log(
        "warn",
        "addVideoListener(): No Shaka event name for " + playerState
      );
      return;
    }
    this.mVideoEventListeners.set(eventName, handler);
    this.mVideoEl.addEventListener(eventName, handler);
  }

  private unregisterShakaEventListeners(): void {
    if (this.mPlayer !== null) {
      this.mShakaEventListeners.forEach((handler, eventType) => {
        if (this.mPlayer !== null) {
          this.mPlayer.removeEventListener(eventType, handler);
        }
      });
    }
    this.mShakaEventListeners.clear();

    if (this.mVideoEl !== null) {
      this.mVideoEventListeners.forEach((handler, eventType) => {
        if (this.mVideoEl !== null) {
          this.mVideoEl.removeEventListener(eventType, handler);
        }
      });
    }
    this.mVideoEventListeners.clear();
  }

  // ── Private: DRM Configuration ─────────────────────────────────────────────

  private configureDrm(drmConfig: TPlayerDrmConfig): void {
    if (this.mPlayer === null) {
      return;
    }
    const drmTypes = drmConfig.drmType;
    const activeDrmType =
      drmTypes !== null && drmTypes !== undefined && drmTypes.length > 0
        ? drmTypes[0]
        : DrmType.Clear;

    if (
      activeDrmType === DrmType.Widevine &&
      drmConfig.widevineConfig !== null &&
      drmConfig.widevineConfig !== undefined
    ) {
      this.configureWidevine(drmConfig.widevineConfig);
    } else if (
      activeDrmType === DrmType.FairPlay &&
      drmConfig.fairplayConfig !== null &&
      drmConfig.fairplayConfig !== undefined
    ) {
      this.configureFairPlay(drmConfig.fairplayConfig);
    }

    this.emitEvent(PlayerState.DRM_CONFIGURED, {
      timestamp: Date.now()
    });
  }

  private configureWidevine(widevineConfig: TWidevineConfig): void {
    if (this.mPlayer === null) {
      return;
    }

    const shakaConfig: Record<string, unknown> = {
      drm: {
        servers: {
          "com.widevine.alpha": widevineConfig.licenseUrl
        }
      }
    };

    if (
      widevineConfig.preferredSecurityLevel !== null &&
      widevineConfig.preferredSecurityLevel !== undefined
    ) {
      const robustness =
        widevineConfig.preferredSecurityLevel === "L1"
          ? "HW_SECURE_ALL"
          : "SW_SECURE_CRYPTO";
      (shakaConfig["drm"] as Record<string, unknown>)["advanced"] = {
        "com.widevine.alpha": {
          videoRobustness: robustness,
          audioRobustness: robustness
        }
      };
    }

    this.mPlayer.configure(shakaConfig);

    if (
      widevineConfig.licenseRequestHeaders !== null &&
      widevineConfig.licenseRequestHeaders !== undefined
    ) {
      this.registerDrmRequestFilter(widevineConfig.licenseRequestHeaders);
    }
  }

  private configureFairPlay(fairplayConfig: TFairPlayConfig): void {
    if (this.mPlayer === null) {
      return;
    }

    this.mPlayer.configure({
      drm: {
        servers: {
          "com.apple.fps": fairplayConfig.licenseUrl
        },
        advanced: {
          "com.apple.fps": {
            serverCertificateUri: fairplayConfig.certificateUrl
          }
        }
      }
    });

    const headers: Record<string, string> = {};

    if (
      fairplayConfig.certificateRequestHeaders !== null &&
      fairplayConfig.certificateRequestHeaders !== undefined
    ) {
      Object.assign(headers, fairplayConfig.certificateRequestHeaders);
    }

    if (
      fairplayConfig.licenseRequestHeaders !== null &&
      fairplayConfig.licenseRequestHeaders !== undefined
    ) {
      Object.assign(headers, fairplayConfig.licenseRequestHeaders);
    }

    if (Object.keys(headers).length > 0) {
      this.registerDrmRequestFilter(headers);
    }
  }

  private registerDrmRequestFilter(headers: Record<string, string>): void {
    if (this.mPlayer === null) {
      return;
    }

    const networkingEngine = this.mPlayer.getNetworkingEngine();
    if (networkingEngine === null) {
      return;
    }
    const licenseRequestType = shaka.net.NetworkingEngine.RequestType.LICENSE;

    const filter: TDrmRequestFilter = (
      type: number,
      request: shaka.extern.Request
    ): void => {
      if (type === licenseRequestType) {
        Object.assign(request.headers, headers);
      }
    };

    this.mDrmRequestFilter = filter;
    networkingEngine.registerRequestFilter(filter);
  }

  private clearDrmRequestFilter(): void {
    if (this.mPlayer === null || this.mDrmRequestFilter === null) {
      return;
    }
    const drmEngine = this.mPlayer.getNetworkingEngine();
    if (drmEngine !== null) {
      drmEngine.unregisterRequestFilter(this.mDrmRequestFilter);
    }
    this.mDrmRequestFilter = null;
  }

  private clearPlaybackUrlRequestFilter(): void {
    if (this.mPlayer === null || this.mPlaybackUrlRequestFilter === null) {
      return;
    }
    const urlEngine = this.mPlayer.getNetworkingEngine();
    if (urlEngine !== null) {
      urlEngine.unregisterRequestFilter(this.mPlaybackUrlRequestFilter);
    }
    this.mPlaybackUrlRequestFilter = null;
  }

  private static interpretLoadFailure(error: unknown): unknown {
    if (typeof error === "object" && error !== null) {
      // LOAD_INTERRUPTED (7000): load() was preempted by a subsequent load()
      // or unload(). Not a real failure — swallow it silently.
      return (error as { code?: number }).code === 7000 ? null : error;
    }

    return new Error(
      "load(): timed out after " +
        String(SHAKA_PLAYER_CONFIG.streaming.loadTimeout) +
        "s waiting for the media element to report loaded metadata"
    );
  }

  /**
   * Give UTC-timing (clock sync) requests a much tighter timeout budget than
   * the rest of the manifest traffic they would otherwise inherit.
   *
   * DashParser awaits this request inline while parsing a live MPD, but treats
   * failure as harmless — it warns and continues with a zero clock offset. So
   * every second spent retrying an unreachable time server is pure added load
   * latency for no benefit.
   *
   * Registered once in init(). Filters live on the NetworkingEngine rather
   * than in the config, so resetConfiguration() does not drop this.
   */
  private registerTimingRetryClamp(): void {
    if (this.mPlayer === null) {
      return;
    }

    const networkingEngine = this.mPlayer.getNetworkingEngine();
    if (networkingEngine === null) {
      return;
    }

    const { TIMING } = shaka.net.NetworkingEngine.RequestType;

    networkingEngine.registerRequestFilter(
      (type: number, request: shaka.extern.Request): void => {
        if (type !== TIMING) {
          return;
        }
        // Replace rather than mutate: NetworkingEngine.makeRequest() assigns
        // retryParameters by reference, so the object handed to us here is the
        // parser's own copy of manifest.retryParameters. Writing through it
        // would clamp every later manifest request too.
        request.retryParameters = {
          ...request.retryParameters,
          ...TIMING_RETRY_OVERRIDES
        };
      }
    );
  }

  /**
   * Reset the live streaming retry backoff whenever a segment downloads, so
   * an outage that has cleared does not leave the next, unrelated one
   * starting from a long delay. Response filters only run on success.
   *
   * Registered once in init(), like registerTimingRetryClamp().
   */
  private registerLiveStreamingFailureReset(): void {
    if (this.mPlayer === null) {
      return;
    }

    const networkingEngine = this.mPlayer.getNetworkingEngine();
    if (networkingEngine === null) {
      return;
    }

    const { SEGMENT } = shaka.net.NetworkingEngine.RequestType;

    networkingEngine.registerResponseFilter((type: number): void => {
      if (type === SEGMENT) {
        this.mLiveStreamingFailureCount = 0;
      }
    });
  }

  private configurePlaybackUrlHeaders(headers: Record<string, string>): void {
    if (this.mPlayer === null) {
      return;
    }

    const networkingEngine = this.mPlayer.getNetworkingEngine();
    if (networkingEngine === null) {
      return;
    }
    const { SEGMENT, MANIFEST } = shaka.net.NetworkingEngine.RequestType;

    const filter: TDrmRequestFilter = (
      type: number,
      request: shaka.extern.Request
    ): void => {
      if (type === SEGMENT || type === MANIFEST) {
        Object.assign(request.headers, headers);
      }
    };

    this.mPlaybackUrlRequestFilter = filter;
    networkingEngine.registerRequestFilter(filter);
  }

  // ── Private: EOS Detection ─────────────────────────────────────────────────

  /**
   * Detects when a live (dynamic) manifest becomes static (VOD).
   * This happens when a live stream ends — the provider stops updating the
   * manifest and marks it as static. When detected, we restart playback at
   * the last known position so the player can reach the new end-of-stream
   * boundary. A guard prevents multiple restart attempts.
   */
  private detectDynamicToStaticTransition(): void {
    if (
      this.mPlayer === null ||
      this.mVideoEl === null ||
      !this.mWasLiveAtLoad ||
      this.mDynamicToStaticRestartAttempted
    ) {
      return;
    }

    // If the stream was live at load but is no longer live, it transitioned.
    if (!this.mPlayer.isLive()) {
      this.mDynamicToStaticRestartAttempted = true;
      this.log(
        "debug",
        "detectDynamicToStaticTransition(): Live manifest became static. " +
          "Restarting at position " +
          String(this.mVideoEl.currentTime)
      );
    }
  }

  // ── Private: Track Mapping ─────────────────────────────────────────────────

  private mapShakaTrackToVariantTrack(
    track: shaka.extern.Track
  ): TVariantTrack {
    return {
      id: track.id,
      active: track.active,
      bandwidth: track.bandwidth,
      language: track.language,
      label: track.label,
      codecs: track.codecs !== null ? track.codecs : "",
      audioCodec: track.audioCodec,
      videoCodec: track.videoCodec,
      width: track.width,
      height: track.height,
      frameRate: track.frameRate,
      channelsCount: track.channelsCount,
      audioId: track.audioId,
      videoId: track.videoId,
      primary: track.primary,
      roles: track.roles
    };
  }

  // ── Private: Statistics ────────────────────────────────────────────────────

  private buildEmptyStats(): TPlayerStats {
    return {
      bufferingTime: NaN,
      bytesDownloaded: NaN,
      completionPercent: NaN,
      corruptedFrames: NaN,
      currentCodecs: "",
      decodedFrames: NaN,
      droppedFrames: NaN,
      drmTimeSeconds: NaN,
      estimatedBandwidth: NaN,
      gapsJumped: NaN,
      height: NaN,
      licenseTime: NaN,
      liveLatency: NaN,
      loadLatency: NaN,
      manifestGapCount: NaN,
      manifestPeriodCount: NaN,
      manifestSizeBytes: NaN,
      manifestTimeSeconds: NaN,
      maxSegmentDuration: NaN,
      nonFatalErrorCount: NaN,
      pauseTime: NaN,
      playTime: NaN,
      stallsDetected: NaN,
      stateHistory: [],
      streamBandwidth: NaN,
      switchHistory: [],
      width: NaN
    };
  }
}
